import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEventConfig, getStepsConfig, getValidationSchema } from './eventConfigs';
import { captureError } from '../../../../utils/sentryHelpers';

// Create the configuration context
const ConfigContext = createContext(null);

// Custom hook to use the configuration context
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

// Configuration Provider Component
export const ConfigProvider = ({ children, eventId, eventConfig: providedConfig, companyId }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  // Get the event configuration - use provided config or fetch by ID
  const eventConfig = useMemo(() => {
    try {
      // If config is provided directly, use it
      if (providedConfig) {
        setConfigError(null);
        return providedConfig;
      }
      // Otherwise, try to get it by eventId
      const config = getEventConfig(eventId);
      setConfigError(null);
      return config;
    } catch (error) {
      console.error('Error loading event configuration:', error);
      captureError(error, {
        feature: 'lecture-registration',
        operation: 'load-event-config',
        consoleMessage: 'Error loading event configuration:',
        context: { eventId }
      });
      setConfigError(error.message);
      return null;
    }
  }, [eventId, providedConfig]);

  // Get the steps configuration with i18n support
  const stepsConfig = useMemo(() => {
    if (!eventConfig) return [];
    try {
      // If eventConfig has stepsConfig function, use it directly
      if (eventConfig.stepsConfig && typeof eventConfig.stepsConfig === 'function') {
        return eventConfig.stepsConfig(t);
      }
      // Otherwise try to get it by eventId
      return getStepsConfig(eventId, t);
    } catch (error) {
      console.error('Error loading steps configuration:', error);
      captureError(error, {
        feature: 'lecture-registration',
        operation: 'load-steps-config',
        consoleMessage: 'Error loading steps configuration:',
        context: { eventId }
      });
      return [];
    }
  }, [eventId, eventConfig, t, i18n.language]);

  // Get the validation schema
  const validationSchema = useMemo(() => {
    if (!eventConfig) return null;
    try {
      // If eventConfig has validationSchema, use it directly
      if (eventConfig.validationSchema) {
        return eventConfig.validationSchema;
      }
      // Otherwise try to get it by eventId
      return getValidationSchema(eventId);
    } catch (error) {
      console.error('Error loading validation schema:', error);
      captureError(error, {
        feature: 'lecture-registration',
        operation: 'load-validation-schema',
        consoleMessage: 'Error loading validation schema:',
        context: { eventId }
      });
      return null;
    }
  }, [eventId, eventConfig]);

  // Helper function to get content in current language
  const getLocalizedContent = (content) => {
    if (!content || !Array.isArray(content)) return '';

    const currentLang = i18n.language || 'en';
    const localizedItem = content.find(item => item.language === currentLang);

    if (localizedItem) return localizedItem.text || localizedItem.content || '';

    // Fallback to English if current language not found
    const englishItem = content.find(item => item.language === 'en');
    if (englishItem) return englishItem.text || englishItem.content || '';

    // Return first available language as last resort
    return content[0]?.text || content[0]?.content || '';
  };

  // Helper function to get conditions for a specific lecture type
  const getConditionsForType = (lectureType) => {
    if (!eventConfig) return [];

    const conditions = [];

    // Check for conditions property directly (used by actions)
    if (eventConfig.conditions && Array.isArray(eventConfig.conditions)) {
      return eventConfig.conditions;
    }

    // Add all general conditions (they apply to all types)
    if (eventConfig.generalConditions) {
      conditions.push(...eventConfig.generalConditions);
    }

    // Add event-specific conditions that apply to this type
    if (eventConfig.eventConditions && lectureType) {
      eventConfig.eventConditions.forEach(condition => {
        if (condition.applicableToTypes?.includes(lectureType)) {
          conditions.push(condition);
        }
      });
    }

    return conditions;
  };

  // Helper function to get validation rules for a specific lecture type
  const getValidationRulesForType = (lectureType) => {
    if (!validationSchema) return {};

    const rules = {
      requiredFields: [...validationSchema.requiredFields],
      customValidations: { ...validationSchema.customValidations }
    };

    // Apply conditional rules based on lecture type
    validationSchema.conditionalRules?.forEach(rule => {
      if (eval(rule.condition.replace('type', `'${lectureType}'`))) {
        // Add additional required fields
        if (rule.additionalRequired) {
          rules.requiredFields.push(...rule.additionalRequired);
        }
        // Override custom validations
        if (rule.customValidations) {
          Object.assign(rules.customValidations, rule.customValidations);
        }
      }
    });

    return rules;
  };

  // Helper function to check if a date is within registration period
  const isRegistrationOpen = () => {
    if (!eventConfig?.dates) return true; // Default to open if no dates configured

    const now = new Date();
    const startDate = new Date(eventConfig.dates.lectureRegistrationStartDate);
    const endDate = new Date(eventConfig.dates.lectureRegistrationEndDate);

    return now >= startDate && now <= endDate;
  };

  // Helper function to check if editing is still allowed
  const isEditingAllowed = (lectureCreatedDate) => {
    if (!eventConfig?.dates) return true; // Default to allowed if no dates configured

    const now = new Date();
    const editEndDate = new Date(eventConfig.dates.lectureEditEndDate);

    return now <= editEndDate;
  };

  // Get information content for the current language
  const getInformationContent = () => {
    // Check for infoTexts first (used by actions)
    if (eventConfig?.infoTexts) {
      const currentLang = i18n.language || 'de';
      const langContent = eventConfig.infoTexts[currentLang] || eventConfig.infoTexts['de'];
      return langContent?.content || '';
    }
    // Fallback to informationContent
    if (!eventConfig?.informationContent) return '';
    return getLocalizedContent(eventConfig.informationContent);
  };

  // Helper function to get step content (heading, description, etc.)
  const getStepContent = (stepId, contentKey) => {
    if (!eventConfig?.stepContent?.[stepId]) {
      console.warn(`No stepContent found for step: ${stepId}`);
      return null;
    }

    const content = eventConfig.stepContent[stepId][contentKey];
    if (!content) {
      console.warn(`No content key "${contentKey}" found for step: ${stepId}`);
      return null;
    }

    return getLocalizedContent(content);
  };

  // Helper to get entity type label (for generic text like "Your lecture/promotion is incomplete")
  const getEntityLabel = (type = 'singular') => {
    if (!eventConfig?.entityTypeLabels?.[type]) {
      return null;
    }

    return getLocalizedContent(eventConfig.entityTypeLabels[type]);
  };

  // Helper to get review section title
  const getReviewSectionTitle = (sectionId) => {
    if (!eventConfig?.reviewSections?.[sectionId]?.title) {
      return null;
    }

    return getLocalizedContent(eventConfig.reviewSections[sectionId].title);
  };

  // Helper to get review field label
  const getReviewLabel = (fieldId) => {
    if (!eventConfig?.reviewLabels?.[fieldId]) {
      return null;
    }

    return getLocalizedContent(eventConfig.reviewLabels[fieldId]);
  };

  // Set loading to false once configuration is loaded
  useEffect(() => {
    if (eventConfig && stepsConfig && validationSchema) {
      setIsLoading(false);
    }
  }, [eventConfig, stepsConfig, validationSchema]);

  // Context value with all configuration data and helper functions
  const contextValue = {
    // Core configuration objects
    eventConfig,
    stepsConfig,
    validationSchema,

    // Event metadata
    eventId: eventConfig?.eventId,
    eventName: eventConfig?.eventName,
    eventType: eventConfig?.eventType,
    companyId,

    // Available options
    availableLectureTypes: eventConfig?.availableLectureTypes || [],

    // Dates
    dates: eventConfig?.dates || {},

    // Type descriptions (used by actions)
    typeDescriptions: eventConfig?.typeDescriptions || {},

    // Conditions
    conditions: eventConfig?.conditions || [],

    // Helper functions
    getLocalizedContent,
    getConditionsForType,
    getValidationRulesForType,
    isRegistrationOpen,
    isEditingAllowed,
    getInformationContent,
    getStepContent,
    getEntityLabel,
    getReviewSectionTitle,
    getReviewLabel,

    // State
    isLoading,
    configError,

    // Language
    currentLanguage: i18n.language
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

// Export everything
export default ConfigProvider;